/**
 * Il dettaglio di un articolo del Magazzino: **la coda di allocazione**.
 *
 * Perché esiste (Enrico, 19-08-2026): cliccando una riga del Magazzino si finiva
 * nel Portafoglio con `?q=CODICE`, cioè con una **ricerca testuale** sul codice
 * articolo. Era il riuso più economico possibile, e sbagliava tre cose:
 *
 * 1. su 515 codici del portafoglio **23 ne catturano altri** (`MS01020-` pesca
 *    anche `MMS01020-00001`), e infatti `MS19014-` prometteva 21 commesse e
 *    apriva una lista di 22;
 * 2. si perdeva il posto — filtro e pagina del Magazzino;
 * 3. soprattutto **rispondeva a un'altra domanda**: nel Magazzino stai guardando
 *    l'offerta («a questo articolo mancano 921 pezzi»), il Portafoglio racconta la
 *    domanda commessa per commessa, e il pezzo che serviva — *chi si prende la
 *    giacenza e chi resta scoperto* — non stava da nessuna parte.
 *
 * Quel pezzo il motore di evadibilità lo calcola a ogni caricamento e lo buttava
 * via: viveva solo dentro il drill-down di una singola riga di commessa. Qui
 * diventa una coda leggibile in un colpo d'occhio, nell'ordine in cui il motore
 * alloca (per data di consegna): le prime prendono la giacenza, poi comincia il
 * piano, e in fondo ci sono quelle scoperte.
 */

import { Ban, ExternalLink, MapPin, X } from "lucide-react";
import { useState } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { useM1DettaglioArticolo } from "@/hooks/useM1";
import { useMagazzinoPallet } from "@/hooks/useMagazzino";
import { num } from "@/lib/numeri";
import { cn } from "@/lib/utils";
import type { M1DettaglioArticolo, M1RigaInAttesaArticolo, MagPallet } from "@/types/api";

const fmt = (v: string | number) => num(Number(v));

/** Quante righe di coda si mostrano. Oltre, la lista diventa uno scorrimento
 *  senza fine dentro un pannello — e le commesse che contano sono le prime,
 *  perché sono quelle che si prendono la giacenza. */
const MAX_CODA = 12;

const MESI = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];

function dataBreve(iso: string | null): string {
  if (!iso) return "—";
  const [, m, g] = iso.split("-").map(Number);
  return `${g} ${MESI[m - 1]}`;
}

/** Da dove viene la merce di questa riga: una parola, non tre numeri da leggere. */
function copertura(r: M1RigaInAttesaArticolo): { testo: string; tono: string; titolo: string } {
  if (!r.valutata) {
    return {
      testo: "non valutata",
      tono: "bg-muted text-muted-foreground",
      titolo: "L'articolo è marcato «materiale di consumo»: il motore non lo valuta",
    };
  }
  if (Number(r.manca) > 0) {
    const parziale = Number(r.da_giacenza) > 0 || Number(r.da_piano_in_tempo) > 0;
    return {
      testo: parziale ? `manca ${fmt(r.manca)}` : "scoperta",
      tono: "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300",
      titolo: "Né la giacenza né il piano coprono questa riga",
    };
  }
  if (Number(r.da_giacenza) >= Number(r.qty_residua)) {
    return {
      testo: "giacenza",
      tono: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
      titolo: "La merce è già a magazzino",
    };
  }
  if (Number(r.da_piano_in_ritardo) > 0) {
    return {
      testo: "piano, tardi",
      tono: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300",
      titolo: "La produzione pianificata arriva dopo la data di consegna",
    };
  }
  return {
    testo: Number(r.da_giacenza) > 0 ? "giacenza + piano" : "piano",
    tono: "bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-300",
    titolo: "Coperta dalla produzione pianificata, in tempo",
  };
}

export function ArticoloDrawer({
  codice,
  onClose,
  onApriCommessa,
  onEscludi,
}: {
  codice: string | null;
  onClose: () => void;
  /** Il salto al Portafoglio resta, ma come **scelta**, non come effetto del clic. */
  onApriCommessa: (numero: number) => void;
  onEscludi: (codice: string) => void;
}) {
  const { data: det, isLoading } = useM1DettaglioArticolo(codice);
  if (!codice) return null;

  return (
    <div className="fixed inset-0 top-14 z-40 flex justify-end" role="dialog">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} aria-hidden />
      <div className="relative z-10 flex h-full w-full max-w-2xl flex-col overflow-y-auto border-l bg-background shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b px-4 py-3">
          <div className="min-w-0">
            <h3 className="font-mono text-[13px] font-semibold">{codice}</h3>
            <p className="truncate text-[11.5px] text-muted-foreground">
              {det?.descrizione ?? "—"}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-muted">
            <X className="size-4" />
          </button>
        </div>

        {isLoading || !det ? (
          <div className="p-4">
            <Skeleton className="h-80 w-full" />
          </div>
        ) : (
          <Corpo
            det={det}
            codice={codice}
            onApriCommessa={onApriCommessa}
            onEscludi={onEscludi}
          />
        )}
      </div>
    </div>
  );
}

function Corpo({
  det,
  codice,
  onApriCommessa,
  onEscludi,
}: {
  det: M1DettaglioArticolo;
  codice: string;
  onApriCommessa: (numero: number) => void;
  onEscludi: (codice: string) => void;
}) {
  const manca = Number(det.manca);
  return (
    <div className="flex-1 space-y-4 p-4">
      {/* La frase che risponde alla domanda con cui hai cliccato. Prima di ogni
          numero: i numeri servono a verificarla, non a ricavarla. */}
      <p className="text-[13px] leading-snug">
        {manca > 0 ? (
          <>
            Di <b>{fmt(det.domanda_aperta)}</b> pezzi richiesti da{" "}
            <b>{det.n_commesse} commesse</b>, <b className="text-red-700 dark:text-red-400">
              {fmt(manca)} non esistono
            </b>{" "}
            né a magazzino né nel piano: restano scoperte{" "}
            <b>{det.n_commesse_scoperte}</b> commesse.
          </>
        ) : (
          <>
            Le <b>{det.n_commesse} commesse</b> che aspettano questo articolo sono{" "}
            <b className="text-emerald-700 dark:text-emerald-400">coperte tutte</b>: {" "}
            {fmt(det.coperto_da_giacenza)} dalla giacenza e {fmt(det.coperto_da_piano)} dal
            piano di produzione.
          </>
        )}
      </p>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Numero label="Richiesti" valore={det.domanda_aperta} sotto="commesse aperte" />
        <Numero
          label="Dalla giacenza"
          valore={det.coperto_da_giacenza}
          sotto={`${fmt(det.giacenza_disponibile)} a magazzino`}
          tono="ok"
        />
        <Numero label="Dal piano" valore={det.coperto_da_piano} sotto="produzione pianificata" />
        <Numero label="Mancano" valore={det.manca} sotto="da nessuna parte" tono="alert" />
      </div>

      {det.escluso_da_evadibilita && (
        <p className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] dark:border-amber-900 dark:bg-amber-950">
          <Ban className="mt-0.5 size-3.5 shrink-0" />
          <span>
            Marcato <b>materiale di consumo</b>
            {det.motivo_esclusione ? ` — ${det.motivo_esclusione}` : ""}: non blocca
            l&apos;evadibilità delle commesse, quindi il motore non lo valuta.
          </span>
        </p>
      )}

      {/* ⚠️ I pallet **prima** della coda (30-08). Il drawer nasce nel Magazzino,
          dove si guarda l'offerta: aprendolo su un articolo con 26 commesse in
          attesa, la coda spingeva la lista dei pallet fuori schermo e la cosa
          per cui si era cliccato bisognava andarla a cercare scorrendo. */}
      <IPallet codice={codice} onApriCommessa={onApriCommessa} />

      <div>
        <p className="mb-1 text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground">
          chi lo aspetta, nell&apos;ordine in cui la merce viene assegnata
        </p>
        <div className="overflow-hidden rounded-lg border">
          <table className="w-full text-[12px]">
            <thead className="bg-muted/40 text-[10px] text-muted-foreground">
              <tr>
                <th className="px-2.5 py-1.5 text-left font-medium">Commessa</th>
                <th className="px-2.5 py-1.5 text-right font-medium">Residua</th>
                <th className="px-2.5 py-1.5 text-left font-medium">Copertura</th>
                <th className="px-2.5 py-1.5 text-left font-medium">Consegna</th>
                <th className="px-2.5 py-1.5 text-left font-medium">Pronta</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {det.righe.slice(0, MAX_CODA).map((r) => {
                const cop = copertura(r);
                return (
                  <tr
                    key={`${r.numero_ordine}-${r.data_consegna}`}
                    className="cursor-pointer hover:bg-brand-50/50 dark:hover:bg-brand-950/30"
                    onClick={() => onApriCommessa(r.numero_ordine)}
                    title="Apre la commessa nel Portafoglio"
                  >
                    <td className="px-2.5 py-1.5">
                      <div className="font-mono text-[11.5px] font-semibold">
                        {r.numero_ordine}
                      </div>
                      <div className="truncate text-[10px] text-muted-foreground">
                        {r.cliente_ragione_sociale ?? "—"}
                      </div>
                    </td>
                    <td className="px-2.5 py-1.5 text-right font-semibold tabular-nums">
                      {fmt(r.qty_residua)}
                    </td>
                    <td className="px-2.5 py-1.5">
                      <span
                        className={cn(
                          "inline-block whitespace-nowrap rounded-full px-1.5 py-0.5 text-[10.5px] font-medium",
                          cop.tono,
                        )}
                        title={cop.titolo}
                      >
                        {cop.testo}
                      </span>
                    </td>
                    <td className="px-2.5 py-1.5 tabular-nums text-muted-foreground">
                      {dataBreve(r.data_consegna)}
                    </td>
                    <td className="px-2.5 py-1.5 tabular-nums text-muted-foreground">
                      {/* La data che il motore calcola: se è dopo la consegna, il
                          ritardo si vede senza doverlo dedurre. */}
                      <span
                        className={cn(
                          r.data_minima_evadibile &&
                            r.data_consegna &&
                            r.data_minima_evadibile > r.data_consegna &&
                            "text-amber-700 dark:text-amber-400",
                        )}
                      >
                        {dataBreve(r.data_minima_evadibile)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {det.righe.length > MAX_CODA && (
            <p className="border-t bg-muted/30 px-2.5 py-1.5 text-[10px] text-muted-foreground">
              e altre {det.righe.length - MAX_CODA} commesse, più lontane nella coda: le
              prime sono quelle che si prendono la merce.
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-t pt-3">
        <button
          type="button"
          onClick={() => onApriCommessa(det.righe[0]?.numero_ordine ?? 0)}
          disabled={!det.righe.length}
          className="inline-flex items-center gap-1.5 rounded border px-2.5 py-1.5 text-[11.5px] hover:bg-accent disabled:opacity-40"
        >
          <ExternalLink className="size-3.5" />
          Apri la prima commessa
        </button>
        {!det.escluso_da_evadibilita && (
          <button
            type="button"
            onClick={() => onEscludi(det.codice_articolo)}
            className="inline-flex items-center gap-1.5 rounded border px-2.5 py-1.5 text-[11.5px] hover:bg-accent"
          >
            <Ban className="size-3.5" />
            Non è prodotto finito
          </button>
        )}
      </div>
    </div>
  );
}

function Numero({
  label,
  valore,
  sotto,
  tono,
}: {
  label: string;
  valore: string;
  sotto: string;
  tono?: "ok" | "alert";
}) {
  const zero = Number(valore) === 0;
  return (
    <div className="rounded-lg border bg-card p-2.5">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p
        className={cn(
          "text-lg font-semibold tabular-nums",
          zero
            ? "text-muted-foreground"
            : tono === "alert"
              ? "text-red-700 dark:text-red-400"
              : tono === "ok"
                ? "text-emerald-700 dark:text-emerald-400"
                : undefined,
        )}
      >
        {fmt(valore)}
      </p>
      <p className="text-[10px] text-muted-foreground">{sotto}</p>
    </div>
  );
}


// ── I pallet ─────────────────────────────────────────────────────────

const STATI: { key: string; label: string; classe: string }[] = [
  { key: "non_ritirato", label: "non ritirato", classe: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300" },
  { key: "impegnato", label: "impegnato", classe: "bg-brand-100 text-brand-900 dark:bg-brand-950 dark:text-brand-300" },
  { key: "libero", label: "libero", classe: "bg-muted text-muted-foreground" },
  { key: "scaduto", label: "scaduto", classe: "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300" },
];

/**
 * La grana fine: **il pallet fisico**, e a chi è destinato.
 *
 * È la risposta alla domanda di Enrico del 30-08 (*«quanti pallet sono già
 * oggetto di commesse che però non vengono ritirate?»*) al livello in cui si
 * può fare qualcosa: non «l'articolo ha 94 pallet di cui 71 impegnati», ma
 * «l'UDM 40119, lotto 261204, corsia E8001, ferma da 210 giorni, aspetta la
 * commessa 1481 di WONDERFOOD che doveva partire a luglio».
 *
 * Ordinati dai più fermi: qui si guarda cosa smaltire, non cosa è arrivato ieri.
 */
function IPallet({
  codice,
  onApriCommessa,
}: {
  codice: string;
  onApriCommessa: (numero: number) => void;
}) {
  const { data: pallet, isLoading } = useMagazzinoPallet(codice);
  const [stato, setStato] = useState<string | null>(null);

  if (isLoading) return <Skeleton className="h-32 w-full" />;
  if (!pallet?.length) return null;

  const presenti = STATI.filter((s) => pallet.some((p) => p.stato === s.key));
  const visibili = stato ? pallet.filter((p) => p.stato === stato) : pallet;

  return (
    <div>
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground">
          i suoi {pallet.length} pallet, dai più fermi
        </p>
        <div className="flex flex-wrap gap-1">
          <Pillola attivo={stato === null} onClick={() => setStato(null)}>
            tutti {pallet.length}
          </Pillola>
          {presenti.map((s) => (
            <Pillola key={s.key} attivo={stato === s.key} onClick={() => setStato(s.key)}>
              {s.label} {pallet.filter((p) => p.stato === s.key).length}
            </Pillola>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border">
        <table className="w-full text-[11.5px]">
          <thead className="bg-muted/40 text-[10px] text-muted-foreground">
            <tr>
              <th className="px-2.5 py-1.5 text-left font-medium">UDM · lotto</th>
              <th className="px-2.5 py-1.5 text-left font-medium">Dov&apos;è</th>
              <th className="px-2.5 py-1.5 text-right font-medium">Pezzi</th>
              <th className="px-2.5 py-1.5 text-right font-medium">Fermo</th>
              <th className="px-2.5 py-1.5 text-left font-medium">Scade</th>
              <th className="px-2.5 py-1.5 text-left font-medium">Per chi</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {visibili.slice(0, 60).map((p) => (
              <RigaPallet key={p.udm ?? `${p.lotto}-${p.ubicazione}`} p={p} onApriCommessa={onApriCommessa} />
            ))}
          </tbody>
        </table>
        {visibili.length > 60 && (
          <p className="border-t bg-muted/30 px-2.5 py-1.5 text-[10px] text-muted-foreground">
            e altri {visibili.length - 60}: il resto si legge filtrando per stato.
          </p>
        )}
      </div>
    </div>
  );
}

/** L'ubicazione arriva come descrizione lunga («Magazzino automatico E80 - E8001»);
 *  la parte che serve a chi va a prendere il pallet è l'ultima. Il testo intero
 *  resta nel `title`, così non si perde niente e la riga sta su una riga. */
function corsia(ubicazione: string | null): string {
  if (!ubicazione) return "—";
  const parti = ubicazione.split(" - ");
  return parti[parti.length - 1] || ubicazione;
}

function RigaPallet({
  p,
  onApriCommessa,
}: {
  p: MagPallet;
  onApriCommessa: (numero: number) => void;
}) {
  const classe = STATI.find((s) => s.key === p.stato)?.classe ?? "bg-muted";
  const prima = p.destinazioni[0];
  return (
    <tr className="align-top">
      <td className="px-2.5 py-1.5">
        <div className="font-mono text-[11px] font-semibold">{p.udm ?? "—"}</div>
        <div className="text-[9.5px] text-muted-foreground">lotto {p.lotto ?? "—"}</div>
      </td>
      <td className="px-2.5 py-1.5">
        <div
          className="flex items-center gap-1 whitespace-nowrap text-[10.5px]"
          title={p.ubicazione ?? undefined}
        >
          <MapPin className="size-3 shrink-0 text-muted-foreground" />
          {corsia(p.ubicazione)}
        </div>
        {p.a_terra && <div className="text-[9.5px] text-muted-foreground">a terra (BC 710)</div>}
      </td>
      <td className="px-2.5 py-1.5 text-right tabular-nums">{fmt(p.quantita)}</td>
      <td className="px-2.5 py-1.5 text-right tabular-nums text-muted-foreground">
        {p.fermo_da_giorni === null ? "—" : `${p.fermo_da_giorni} g`}
      </td>
      <td className="px-2.5 py-1.5 whitespace-nowrap">
        <span className="tabular-nums">{dataBreve(p.data_scadenza)}</span>
        {p.residua_pct !== null && (
          <span
            className="ml-1 text-[9.5px] text-muted-foreground"
            title={`${Math.round(p.residua_pct)}% di vita residua · shelf life ${p.shelf_life_mesi} mesi · prodotto il ${p.data_produzione ?? "—"}`}
          >
            {Math.round(p.residua_pct)}%
          </span>
        )}
      </td>
      <td className="px-2.5 py-1.5">
        <span className={cn("inline-block rounded-full px-1.5 py-0.5 text-[10px] font-medium", classe)}>
          {STATI.find((s) => s.key === p.stato)?.label}
        </span>
        {prima && (
          <button
            type="button"
            onClick={() => onApriCommessa(prima.numero_ordine)}
            className="mt-0.5 block max-w-[150px] truncate text-left text-[9.5px] text-brand-700 underline-offset-2 hover:underline dark:text-brand-400"
            title={`Commessa ${prima.numero_ordine} — consegna ${prima.data_consegna}${p.destinazioni.length > 1 ? ` (e altre ${p.destinazioni.length - 1})` : ""}`}
          >
            {prima.numero_ordine} · {prima.cliente ?? "—"}
            {p.destinazioni.length > 1 && ` +${p.destinazioni.length - 1}`}
          </button>
        )}
      </td>
    </tr>
  );
}

function Pillola({
  attivo,
  onClick,
  children,
}: {
  attivo: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-2 py-0.5 text-[10px] transition-colors",
        attivo ? "border-brand-600 bg-brand-50 font-medium text-brand-800 dark:bg-brand-950 dark:text-brand-300" : "hover:bg-accent"
      )}
    >
      {children}
    </button>
  );
}
